import {
  InjectableScript,
  InjectableScripts,
  Script,
  ScriptId,
  ScriptInjectId,
  ScriptInjectionTarget,
  StepScriptType
} from '@core/data-access/models/script-injector.model';
import { concat, forEach, isEmpty, isNil, map, nth, some, startsWith, trim } from 'lodash';

export const toStepScriptId = (scriptType: StepScriptType): ScriptId =>
  `${ScriptInjectId.EventStepScript}-${scriptType}`;

export const hasInjectedScript = (document: Document, id: ScriptId) => !isNil(document.querySelector(`[id^="${id}-"]`));

export const injectScriptElement = (
  document: Document,
  scriptCode: Script,
  scriptId: ScriptId,
  totalTicketsPrice?: string,
  orderId?: string
) => {
  const scripts = toInjectableScripts(scriptCode, scriptId, totalTicketsPrice, orderId);
  const targets: Record<ScriptInjectionTarget, HTMLElement> = {
    [ScriptInjectionTarget.Head]: document.head,
    [ScriptInjectionTarget.Body]: document.body
  };

  forEach(scripts, (script) => targets[script.target].appendChild(createScriptElement(document, script)));
};

const toInjectableScripts = (
  script: Script,
  scriptId: ScriptId,
  totalTicketsPrice?: string,
  orderId?: string
): InjectableScripts => {
  const scriptTags: Omit<InjectableScript, 'id'>[] = [];

  const scriptTagRegex = /<script[^>]*>([\s\S]*?)<\/script>/gi;
  const scriptSrcRegex = /src=["'](.*?)["']/i;

  const target = some([ScriptInjectId.OperatorsScript, ScriptInjectId.EventAllPagesScript], (headScriptId) =>
    startsWith(scriptId, headScriptId)
  )
    ? ScriptInjectionTarget.Head
    : ScriptInjectionTarget.Body;

  const plainScript = trim(
    script.replace(scriptTagRegex, (scriptTag: string, innerHtml: string) => {
      const src = trim(nth(scriptSrcRegex.exec(scriptTag), 1));
      const trimmedInnerHtml = trim(innerHtml);
      const isSrcWithInnerHtml = !isEmpty(src) && !isEmpty(trimmedInnerHtml);
      const scriptInnerHtml = isSrcWithInnerHtml ? '' : trimmedInnerHtml;

      scriptTags.push(toInjectableScript(src, scriptInnerHtml, target, totalTicketsPrice, orderId));

      if (isSrcWithInnerHtml) {
        scriptTags.push(toInjectableScript('', trimmedInnerHtml, target, totalTicketsPrice, orderId));
      }

      return '';
    })
  );

  const plainInjectableScript = toInjectableScript('', plainScript, target, totalTicketsPrice, orderId);
  const injectableScripts = isEmpty(plainScript) ? scriptTags : concat(plainInjectableScript, scriptTags);

  return map(injectableScripts, (script, index) => ({ ...script, id: `${scriptId}-${index + 1}` }));
};

const toInjectableScript = (
  src: string,
  innerHtml: string,
  target: ScriptInjectionTarget,
  totalTicketsPrice?: string,
  orderId?: string
) => ({
  src,
  innerHtml: replaceInnerHtmlPlaceholders(innerHtml, totalTicketsPrice, orderId),
  target
});

const replaceInnerHtmlPlaceholders = (innerHtml: string, totalTicketsPrice?: string, orderId?: string) => {
  if (isEmpty(innerHtml)) {
    return innerHtml;
  }

  let replacedInnerHtml = innerHtml;

  if (!isNil(totalTicketsPrice)) {
    replacedInnerHtml = replacedInnerHtml.replace(/\[Revenue\]/gim, totalTicketsPrice);
  }

  if (!isNil(orderId)) {
    replacedInnerHtml = replacedInnerHtml.replace(/\[OrderID\]/gim, orderId);
  }

  return replacedInnerHtml;
};

const createScriptElement = (document: Document, { id, src, innerHtml }: InjectableScript) => {
  const scriptElement = document.createElement('script');

  scriptElement.id = id;
  scriptElement.async = false;

  if (!isEmpty(src)) {
    scriptElement.src = src;
  }

  if (!isEmpty(innerHtml)) {
    scriptElement.textContent = innerHtml;
  }

  return scriptElement;
};

export const injectIframeElement = (document: Document, iframeUrl: string, id: string) => {
  const body = document.body || document.getElementsByTagName('body')[0];
  const iframe = document.createElement('iframe');

  if (id) {
    const oldIframe = document.getElementById(id);

    if (oldIframe) {
      body.removeChild(oldIframe);
    }

    const randomNumber = Math.random() * 10000000000000;
    iframe.id = id;

    iframe.src = iframeUrl + randomNumber.toString() + '?';
    iframe.width = '1';
    iframe.height = '1';
    iframe.frameBorder = '0';
    iframe.style.display = 'none';
  }

  body.appendChild(iframe);
};

export const injectStyleElement = (document: Document, css: string, id: string) => {
  const head = document.head || document.getElementsByTagName('head')[0];
  const style = document.createElement('style');

  if (id) {
    const oldCSS = document.getElementById(id);

    if (oldCSS) {
      oldCSS.remove();
    }

    style.id = id;
  }

  style.appendChild(document.createTextNode(css));

  head.appendChild(style);
};

export const setPageBackgroundImage = (
  document: Document,
  isHighContrastEnabled: boolean,
  backgroundImageUrl: string
) => {
  if (isEmpty(backgroundImageUrl)) {
    return;
  }

  const backgroundImage = document.querySelector('.page') as HTMLElement;

  if (isNil(backgroundImage)) {
    return;
  }

  backgroundImage.style.backgroundImage = isHighContrastEnabled ? 'none' : `url('${backgroundImageUrl}')`;
};
